import { useRef, useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Upload, MapPin, Crown, RefreshCw, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

const DEFAULT_MAP = '/maps/default-quest-map.jpg';

/**
 * JourneyMapEditor — visual canvas where the teacher positions mission pins
 * over a map background. Supports default map or custom image upload.
 *
 * Props:
 *  - mapImageUrl: current background image (data URL or path). null → default
 *  - missions: [{ order, title, map_x?, map_y?, is_climax? }]
 *  - onChange: ({ mapImageUrl, missions }) => void
 */
export default function JourneyMapEditor({ mapImageUrl, missions, onChange }) {
  const mapRef = useRef(null);
  const fileRef = useRef(null);
  const [draggingIdx, setDraggingIdx] = useState(null);
  const [previewSrc, setPreviewSrc] = useState(mapImageUrl || DEFAULT_MAP);

  useEffect(() => {
    setPreviewSrc(mapImageUrl || DEFAULT_MAP);
  }, [mapImageUrl]);

  const bounds = () => mapRef.current?.getBoundingClientRect();

  // Auto-place pins along a curve when missions don't have coordinates yet
  const ensurePositions = useCallback(() => {
    const positioned = missions.map((m, i) => {
      if (typeof m.map_x === 'number' && typeof m.map_y === 'number') return m;
      // distribute on a gentle diagonal S-curve
      const t = missions.length > 1 ? i / (missions.length - 1) : 0.5;
      const x = 15 + t * 70; // 15% to 85%
      const y = 80 - Math.sin(t * Math.PI) * 55; // gentle arc
      return { ...m, map_x: x, map_y: y };
    });
    if (JSON.stringify(positioned) !== JSON.stringify(missions)) {
      onChange({ mapImageUrl: previewSrc === DEFAULT_MAP ? null : previewSrc, missions: positioned });
    }
  }, [missions, previewSrc, onChange]);

  useEffect(() => { ensurePositions(); }, [ensurePositions]);

  const handlePointerDown = (e, idx) => {
    e.preventDefault();
    setDraggingIdx(idx);
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e) => {
    if (draggingIdx === null) return;
    const b = bounds();
    if (!b) return;
    const x = ((e.clientX - b.left) / b.width) * 100;
    const y = ((e.clientY - b.top) / b.height) * 100;
    const newX = Math.max(2, Math.min(98, x));
    const newY = Math.max(2, Math.min(98, y));
    const next = missions.map((m, i) => i === draggingIdx ? { ...m, map_x: newX, map_y: newY } : m);
    onChange({ mapImageUrl: previewSrc === DEFAULT_MAP ? null : previewSrc, missions: next });
  };

  const handlePointerUp = () => setDraggingIdx(null);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Imagem muito grande (máx 5 MB)');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      setPreviewSrc(dataUrl);
      onChange({ mapImageUrl: dataUrl, missions });
      toast.success('Mapa personalizado carregado!', { icon: '🗺️' });
    };
    reader.readAsDataURL(file);
  };

  const useDefault = () => {
    setPreviewSrc(DEFAULT_MAP);
    onChange({ mapImageUrl: null, missions });
    toast.message('Usando o mapa padrão sugerido', { icon: '🗺️' });
  };

  const resetPositions = () => {
    const reset = missions.map((m, i) => {
      const t = missions.length > 1 ? i / (missions.length - 1) : 0.5;
      const x = 15 + t * 70;
      const y = 80 - Math.sin(t * Math.PI) * 55;
      return { ...m, map_x: x, map_y: y };
    });
    onChange({ mapImageUrl: previewSrc === DEFAULT_MAP ? null : previewSrc, missions: reset });
    toast.success('Posições resetadas');
  };

  return (
    <div className="space-y-3" data-testid="journey-map-editor">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs text-slate-600">
          <strong>Arraste cada pin</strong> para posicionar onde a missão acontece no mapa. A linha tracejada mostra a rota.
        </div>
        <div className="flex flex-wrap gap-2">
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onUpload} data-testid="map-upload-input"/>
          <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} data-testid="map-upload-btn">
            <Upload className="w-3.5 h-3.5 mr-1"/> Enviar meu mapa
          </Button>
          <Button size="sm" variant="ghost" onClick={useDefault} data-testid="map-use-default-btn">
            <ImageIcon className="w-3.5 h-3.5 mr-1"/> Mapa padrão
          </Button>
          <Button size="sm" variant="ghost" onClick={resetPositions} data-testid="map-reset-positions-btn">
            <RefreshCw className="w-3.5 h-3.5 mr-1"/> Reposicionar
          </Button>
        </div>
      </div>

      <div
        ref={mapRef}
        className="relative w-full aspect-[4/3] rounded-2xl overflow-hidden border-[3px] border-slate-900 shadow-[3px_3px_0_rgba(15,23,42,0.85)] bg-slate-200 select-none"
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
        style={{
          backgroundImage: `url(${previewSrc})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="journey-map-canvas"
      >
        {/* Dashed connector SVG between mission pins (in order) */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none" preserveAspectRatio="none" viewBox="0 0 100 100">
          {missions.length > 1 && (() => {
            const pts = missions
              .filter((m) => typeof m.map_x === 'number' && typeof m.map_y === 'number')
              .map((m) => `${m.map_x},${m.map_y}`)
              .join(' ');
            return (
              <polyline
                points={pts}
                fill="none"
                stroke="#0f172a"
                strokeWidth="0.6"
                strokeDasharray="1.5 1.2"
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity="0.85"
              />
            );
          })()}
        </svg>

        {/* Mission pins */}
        {missions.map((m, i) => {
          const x = typeof m.map_x === 'number' ? m.map_x : 50;
          const y = typeof m.map_y === 'number' ? m.map_y : 50;
          const isClimax = m.is_climax || i === missions.length - 1;
          return (
            <button
              type="button"
              key={i}
              data-testid={`map-pin-${i}`}
              onPointerDown={(e) => handlePointerDown(e, i)}
              style={{ left: `${x}%`, top: `${y}%` }}
              className={`absolute -translate-x-1/2 -translate-y-full touch-none cursor-grab active:cursor-grabbing ${draggingIdx === i ? 'z-30 scale-110' : 'z-20'}`}
            >
              {/* Pin */}
              <div className={`relative flex flex-col items-center`}>
                <div className={`rounded-full border-[3px] border-slate-900 shadow-[2px_2px_0_rgba(15,23,42,0.85)] grid place-items-center w-9 h-9 ${
                  isClimax ? 'bg-amber-400' : 'bg-rose-500'
                }`}>
                  {isClimax
                    ? <Crown className="w-4 h-4 text-slate-900"/>
                    : <MapPin className="w-4 h-4 text-white"/>}
                </div>
                {/* Order badge */}
                <span className="absolute -top-1 -left-1 w-4 h-4 bg-slate-900 text-white text-[9px] font-outfit font-black rounded-full grid place-items-center border-2 border-white">{i + 1}</span>
                {/* Title chip */}
                <span className="mt-1 px-1.5 py-0.5 rounded-md bg-white/95 border-2 border-slate-900 text-[10px] font-outfit font-black text-slate-900 whitespace-nowrap max-w-[120px] truncate shadow-sm">
                  {m.title || `Missão ${i + 1}`}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      <p className="text-[11px] text-slate-500 italic">
        Dica: clique e arraste qualquer pin para reposicionar. A ordem dos pins (e da linha tracejada) segue a ordem das missões.
      </p>
    </div>
  );
}
